/**
 * Shared Tasks-page primitives, ported from the design's tasks.jsx
 * (Dropdown shell, status / priority / date chips, menu rows, empty view,
 * confirm dialog, editable column header).
 */
import { useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import {
  ArrowDown,
  ArrowUp,
  CalendarDays,
  Check,
  ChevronDown,
  Flag,
  Info,
  Pencil,
  TriangleAlert,
  X,
  type LucideIcon,
} from "lucide-react";
import { cn } from "@weco/ui";
import {
  DATE_QUICK_PICKS,
  PRIORITIES,
  PRIORITY_META,
  STATUSES,
  STATUS_META,
  formatDue,
  quickPickDate,
  type TaskPriority,
  type TaskStatus,
} from "./model";
import { taskDueLabel, taskPriorityLabel, taskStatusLabel } from "./lib/taskLabels";

// ── Dropdown shell ──────────────────────────────────────────────────────────
// Portalled + fixed-positioned to its trigger so it escapes overflow-hidden
// containers (tables, modals, the drawer). `align` is logical: in RTL the
// "start" edge is the right one.
interface DropdownProps {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  className?: string;
  align?: "start" | "end";
  up?: boolean;
  matchWidth?: boolean;
}

export function Dropdown({ open, onClose, children, className, align = "start", up = false, matchWidth = false }: DropdownProps) {
  const markerRef = useRef<HTMLSpanElement>(null);
  const [style, setStyle] = useState<CSSProperties | null>(null);

  useLayoutEffect(() => {
    if (!open) return;
    const anchor = markerRef.current?.parentElement;
    if (!anchor) return;
    const compute = () => {
      const r = anchor.getBoundingClientRect();
      const isRtl = document.documentElement.dir === "rtl";
      const alignRight = isRtl ? align === "start" : align === "end";
      const s: CSSProperties = { position: "fixed", zIndex: 71, maxWidth: "calc(100vw - 16px)" };
      if (up) s.bottom = window.innerHeight - r.top + 4;
      else s.top = r.bottom + 4;
      if (alignRight) s.right = window.innerWidth - r.right;
      else s.left = r.left;
      if (matchWidth) s.width = r.width;
      setStyle(s);
    };
    compute();
    window.addEventListener("scroll", compute, true);
    window.addEventListener("resize", compute);
    return () => {
      window.removeEventListener("scroll", compute, true);
      window.removeEventListener("resize", compute);
    };
  }, [open, align, up, matchWidth]);

  if (!open) return <span ref={markerRef} style={{ display: "none" }} />;

  return (
    <>
      <span ref={markerRef} style={{ display: "none" }} />
      {createPortal(
        <>
          <div
            className="fixed inset-0 z-[70]"
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
          />
          <div
            role="menu"
            style={style ?? { position: "fixed", opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
            className={cn("bg-background border border-border rounded-xl shadow-2xl py-1 anim-scale-in", className)}
          >
            {children}
          </div>
        </>,
        document.body,
      )}
    </>
  );
}

// ── Menu row ────────────────────────────────────────────────────────────────
export function MenuItem({
  icon: Icon,
  label,
  onClick,
  danger,
  trailing,
}: {
  icon?: LucideIcon;
  label: ReactNode;
  onClick: () => void;
  danger?: boolean;
  trailing?: ReactNode;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      className={cn(
        "w-full flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-lg text-start transition-colors",
        danger ? "text-red-500 hover:bg-red-500/10" : "text-foreground hover:bg-muted",
      )}
    >
      {Icon && <Icon className={cn("size-4 shrink-0", !danger && "text-muted-foreground")} />}
      <span className="flex-1">{label}</span>
      {trailing}
    </button>
  );
}

export function MenuSeparator() {
  return <div className="my-1 h-px bg-border mx-2" />;
}

// ── Status chip ─────────────────────────────────────────────────────────────
export function StatusChip({
  status,
  onChange,
  size = "sm",
}: {
  status: TaskStatus;
  onChange: (s: TaskStatus) => void;
  size?: "sm" | "md";
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const meta = STATUS_META[status];
  const pad = size === "sm" ? "text-[10px] px-2 py-0.5" : "text-xs px-3 py-1";
  return (
    <div className="relative inline-block">
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((o) => !o);
        }}
        aria-haspopup="menu"
        aria-expanded={open}
        className={cn("flex items-center gap-2 font-bold rounded tracking-wide whitespace-nowrap", pad, meta.chip)}
      >
        <span className={cn("w-1.5 h-1.5 rounded-full shrink-0", meta.dot)} />
        {taskStatusLabel(status, t)}
      </button>
      <Dropdown open={open} onClose={() => setOpen(false)} className="w-44">
        {STATUSES.map((s) => (
          <button
            key={s}
            type="button"
            role="menuitemradio"
            aria-checked={s === status}
            onClick={(e) => {
              e.stopPropagation();
              onChange(s);
              setOpen(false);
            }}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-muted text-start"
          >
            <span className={cn("w-2 h-2 rounded-full shrink-0", STATUS_META[s].dot)} />
            <span className="flex-1 font-medium">{taskStatusLabel(s, t)}</span>
            {s === status && <Check className="size-[13px] text-primary" />}
          </button>
        ))}
      </Dropdown>
    </div>
  );
}

// ── Priority chip ───────────────────────────────────────────────────────────
export function PriorityChip({
  priority,
  onChange,
  iconOnly,
}: {
  priority: TaskPriority;
  onChange: (p: TaskPriority) => void;
  iconOnly?: boolean;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const meta = PRIORITY_META[priority];
  const label = taskPriorityLabel(priority, t);
  return (
    <div className="relative inline-block">
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((o) => !o);
        }}
        title={label}
        aria-label={iconOnly ? label : undefined}
        aria-haspopup="menu"
        aria-expanded={open}
        className={cn("flex items-center gap-1 rounded px-2 py-1 hover:bg-muted transition-colors", meta.color)}
      >
        <Flag className="size-3.5 opacity-80" />
        {!iconOnly && <span className="text-xs font-semibold">{label}</span>}
      </button>
      <Dropdown open={open} onClose={() => setOpen(false)} className="w-36">
        {PRIORITIES.map((p) => (
          <button
            key={p}
            type="button"
            role="menuitemradio"
            aria-checked={p === priority}
            onClick={(e) => {
              e.stopPropagation();
              onChange(p);
              setOpen(false);
            }}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-muted text-start"
          >
            <Flag className={cn("size-[13px]", PRIORITY_META[p].color)} />
            <span className="flex-1 font-medium">{taskPriorityLabel(p, t)}</span>
            {p === priority && <Check className="size-[13px] text-primary" />}
          </button>
        ))}
      </Dropdown>
    </div>
  );
}

// ── Date menu (quick picks + exact date) ────────────────────────────────────
// The design offers relative quick picks only; the backend stores a real
// calendar date, so each pick resolves to a date and an exact-date input is
// kept for anything else.
export function DateMenuContent({
  value,
  onPick,
  noneLabel,
}: {
  value: string | null;
  onPick: (iso: string | null) => void;
  noneLabel?: string;
}) {
  const { t } = useTranslation();
  return (
    <>
      {DATE_QUICK_PICKS.map((o) => {
        const iso = quickPickDate(o);
        return (
          <button
            key={o}
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onPick(iso);
            }}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-muted text-start rounded-lg"
          >
            <CalendarDays className="size-4 text-muted-foreground shrink-0" />
            <span className="flex-1 font-medium">{taskDueLabel(o, t)}</span>
            {iso === value && <Check className="size-[13px] text-primary" />}
          </button>
        );
      })}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onPick(null);
        }}
        className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-muted text-start rounded-lg"
      >
        <CalendarDays className="size-4 text-muted-foreground shrink-0" />
        <span className="flex-1 font-medium">{noneLabel ?? t("tasks.due.noDateShort", { defaultValue: "No date" })}</span>
        {value === null && <Check className="size-[13px] text-primary" />}
      </button>
      <div className="mx-2 my-1 h-px bg-border" />
      <label className="flex items-center gap-2 px-3 py-2 text-xs font-semibold text-muted-foreground">
        <span className="shrink-0">{t("tasks.due.pickDate", { defaultValue: "Pick a date" })}</span>
        <input
          type="date"
          value={value ?? ""}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => onPick(e.target.value || null)}
          className="flex-1 min-w-0 h-8 px-2 bg-muted/40 border border-border rounded-lg text-xs text-foreground outline-none focus:border-primary"
        />
      </label>
    </>
  );
}

export function DateChip({ value, onChange }: { value: string | null; onChange: (iso: string | null) => void }) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  return (
    <div className="relative inline-block">
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((o) => !o);
        }}
        aria-haspopup="menu"
        aria-expanded={open}
        className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground px-2 py-0.5 rounded hover:bg-muted transition-colors font-medium"
      >
        <CalendarDays className="size-[13px]" />
        {formatDue(value, t, i18n.language)}
      </button>
      <Dropdown open={open} onClose={() => setOpen(false)} className="w-56 p-1">
        <DateMenuContent
          value={value}
          onPick={(iso) => {
            onChange(iso);
            setOpen(false);
          }}
        />
      </Dropdown>
    </div>
  );
}

// ── Empty view ──────────────────────────────────────────────────────────────
export function EmptyView({
  icon: Icon,
  title,
  desc,
  action,
}: {
  icon: LucideIcon;
  title: ReactNode;
  desc: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="p-8 h-full flex flex-col items-center justify-center text-center">
      <div className="w-16 h-16 bg-muted/50 rounded-full flex items-center justify-center mb-4">
        <Icon className="size-8 text-muted-foreground" />
      </div>
      <h2 className="text-lg font-bold mb-2">{title}</h2>
      <p className="text-sm text-muted-foreground max-w-sm">{desc}</p>
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

// ── Confirm dialog (design: tasks-hubs.jsx ConfirmDialog) ───────────────────
export function ConfirmDialog({
  title,
  body,
  confirmLabel,
  danger = true,
  onConfirm,
  onClose,
}: {
  title: ReactNode;
  body: ReactNode;
  confirmLabel: ReactNode;
  danger?: boolean;
  onConfirm: () => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        role="alertdialog"
        aria-modal="true"
        className="bg-background border border-border rounded-2xl shadow-2xl w-full max-w-md anim-scale-in"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 pb-4">
          <div
            className={cn(
              "w-10 h-10 rounded-xl border border-border flex items-center justify-center mb-5",
              danger ? "text-red-500 bg-red-500/10 border-red-500/20" : "text-muted-foreground bg-muted/30",
            )}
          >
            {danger ? <TriangleAlert className="size-5" /> : <Info className="size-5" />}
          </div>
          <h2 className="text-xl font-bold mb-2">{title}</h2>
          <p className="text-muted-foreground text-sm leading-relaxed">{body}</p>
        </div>
        <div className="flex gap-3 p-4 pt-0">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 py-3 px-4 rounded-xl border border-border font-semibold text-sm hover:bg-muted transition-colors"
          >
            {t("tasks.cancel", { defaultValue: "Cancel" })}
          </button>
          <button
            type="button"
            onClick={() => {
              onConfirm();
              onClose();
            }}
            className={cn(
              "flex-1 py-3 px-4 rounded-xl font-semibold text-sm transition-colors",
              danger ? "bg-red-500 text-white hover:bg-red-600" : "bg-foreground text-background hover:opacity-90",
            )}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Editable / sortable column header ───────────────────────────────────────
export function HeaderCell({
  label,
  sortDir,
  onRename,
  onSort,
}: {
  label: string;
  sortDir: "asc" | "desc" | null;
  onRename?: (label: string) => void;
  onSort?: (dir: "asc" | "desc" | null) => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  if (editing) {
    return (
      <input
        autoFocus
        defaultValue={label}
        aria-label={t("tasks.columns.rename", { defaultValue: "Rename" })}
        onBlur={(e) => {
          onRename?.(e.target.value.trim() || label);
          setEditing(false);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") e.currentTarget.blur();
          if (e.key === "Escape") setEditing(false);
        }}
        onClick={(e) => e.stopPropagation()}
        className="w-full text-xs font-semibold bg-foreground/[0.06] border border-primary rounded px-2 py-1 outline-none"
      />
    );
  }
  return (
    <div className="relative inline-flex items-center gap-1 group/col max-w-full">
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((o) => !o);
        }}
        aria-haspopup="menu"
        aria-expanded={open}
        className="inline-flex items-center gap-1 min-w-0 hover:text-foreground transition-colors"
      >
        <span className="truncate">{label}</span>
        {sortDir &&
          (sortDir === "asc" ? (
            <ArrowUp className="size-3 text-primary shrink-0" />
          ) : (
            <ArrowDown className="size-3 text-primary shrink-0" />
          ))}
        <ChevronDown
          className={cn(
            "size-[13px] shrink-0 transition-all",
            open ? "opacity-100" : "opacity-0 group-hover/col:opacity-100",
          )}
        />
      </button>
      <Dropdown open={open} onClose={() => setOpen(false)} className="w-44 p-1">
        {onRename && (
          <MenuItem
            icon={Pencil}
            label={t("tasks.columns.rename", { defaultValue: "Rename" })}
            onClick={() => {
              setEditing(true);
              setOpen(false);
            }}
          />
        )}
        {onSort && (
          <>
            <MenuItem
              icon={ArrowUp}
              label={t("tasks.columns.sortAsc", { defaultValue: "Sort ascending" })}
              onClick={() => {
                onSort("asc");
                setOpen(false);
              }}
            />
            <MenuItem
              icon={ArrowDown}
              label={t("tasks.columns.sortDesc", { defaultValue: "Sort descending" })}
              onClick={() => {
                onSort("desc");
                setOpen(false);
              }}
            />
            {sortDir && (
              <MenuItem
                icon={X}
                label={t("tasks.columns.clearSort", { defaultValue: "Clear sort" })}
                onClick={() => {
                  onSort(null);
                  setOpen(false);
                }}
              />
            )}
          </>
        )}
      </Dropdown>
    </div>
  );
}
